import React, { useEffect, useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Components
import DataTable from '../../components/DataTable';
import UserDetails from '../../components/popup/UserDetails';
import { ActionButtons, Switch } from '../../components/ActionButtons';
import AddEditInterviewer from './AddEditInterviewer';

// Redux-Actions
import { userDetailsAction } from '../../redux/actions/userDetails';
import {
  getInterviewersList,
  deleteInterviewer,
  userStatus
} from '../../redux/actions/interviewers';

// Prime-React Components
import { confirmDialog } from 'primereact/confirmdialog';

// Constants
import {
  DEBOUNCE_TIMEOUT,
  INITIAL_PAGE_NUMBER,
  INITIAL_SIZE,
  ACTION_TYPE,
  TABLE_NAME,
  INTERVIEWER_FILTER_KEY
} from '../../constants/common';
import { INTERVIEWERS_COLUMNS } from '../../constants/tableColumns';

// Utils
import {
  wordCapitalize,
  getUserRole,
  formatExperience,
  isDarkMode,
  normalizeObject
} from '../../utils/common';
import { debounce } from '../../utils/debounce';

// Apis
import { editUserDetailsAction } from '../../redux/actions/admin';
import { formatTime, getFormattedDateView } from '../../utils/date';
import { Chip } from '@mui/material';
import EditInterviewerAvailability from '../../components/popup/EditInterviewerAvailability';
import { INTERVIEWER_FILTER_DROPDOWN_OPTIONS } from '../../constants/dropdownOptions';
import CopyToClipboard from '../../components/CopyToClipboard';

const interviewersTableStates = {
  searchValue: '',
  pageNumber: INITIAL_PAGE_NUMBER,
  size: INITIAL_SIZE,
  sortBy: 'DateModified',
  technology: '',
  orderBy: 'desc',
  practicalOptionFilter: null
};

const InterviewerList = () => {
  const dispatch = useDispatch();
  const technologyListData = useSelector((state) => state.hrs.interviewTechnologyType);
  const { interviewers, userDetails } = useSelector((state) => state);
  const [interviewerId, setInterviewerId] = useState(null);
  const [editInterviewerId, setEditInterviewerId] = useState(null);
  const [interviewerList, setInterviewerList] = useState([]);
  const [interviewerExport, setInterviewerExport] = useState([]);
  const [interviewerDetails, setInterviewerDetails] = useState([]);
  // const [tableStates, setTableStates] = useState(interviewersTableStates);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedInterviewerName, setSelectedInterviewerName] = useState(null);
  const [showEditInterviewerAvailabilityPopup, setShowEditInterviewerAvailabilityPopup] =
    useState(false);
  const onTogglePopup = () => setShowPopup((prevState) => !prevState);
  const [visibleRight, setVisibleRight] = useState(false);
  const onToggleSiderbar = () => setVisibleRight(!visibleRight);

  const normalizeTableState = (state) => {
    if (!state) return interviewersTableStates;

    return {
      ...interviewersTableStates,
      ...state,
      technology: state.technology || '',
      practicalOptionFilter: state.practicalOptionFilter || null
    };
  };

  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(INTERVIEWER_FILTER_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  };
  const [tableStates, setTableStates] = useState(() => {
    const saved = getSavedFilters();
    return saved ? normalizeTableState(saved) : interviewersTableStates;
  });

  useEffect(() => {
    const { searchValue, technology, practicalOptionFilter } = tableStates;

    const isFilterApplied =
      searchValue?.trim() !== '' ||
      (Array.isArray(technology) ? technology.length > 0 : technology?.trim() !== '') ||
      practicalOptionFilter !== null;

    if (isFilterApplied) {
      localStorage.setItem(INTERVIEWER_FILTER_KEY, JSON.stringify(tableStates));
    } else {
      localStorage.removeItem(INTERVIEWER_FILTER_KEY);
    }
  }, [tableStates]);

  useEffect(() => {
    dispatch(getInterviewersList(tableStates));
    if (interviewerId && interviewerId === editInterviewerId) {
      dispatch(userDetailsAction(interviewerId));
    }
  }, [
    dispatch,
    tableStates,
    interviewers?.isDeleted,
    interviewers?.isUpdated,
    interviewers?.isAdded
  ]);

  useEffect(() => {
    const modifiedList = interviewers?.data?.map((user, index) => {
      const name = `${wordCapitalize(user.FirstName)} ${wordCapitalize(user.LastName)}`;
      return {
        ...user,
        Experience: formatExperience(user?.YearsOfExperience, user?.MonthsOfExperience),
        Name: (
          <span className="table-view-popup-link" onClick={() => handleView(user.ID)}>
            {name}
          </span>
        ),
        Practical: (
          <Chip
            color={user?.IsPracticalAllow ? 'success' : 'error'}
            label={user?.IsPracticalAllow ? 'Yes' : 'No'}
          />
        ),
        PracticalAdmin: (
          <Chip
            color={user?.IsPracticalAdmin ? 'success' : 'error'}
            label={user?.IsPracticalAdmin ? 'Yes' : 'No'}
          />
        ),
        'Technical Skills': (
          <ul className="technology-list" key={index}>
            {user.UMap && user?.UMap?.length > 0
              ? user.UMap?.map((tech) => (
                  <li
                    key={tech?.MapT?.ID}
                    className="technology-badge"
                    style={{
                      backgroundColor: `${
                        isDarkMode() ? tech?.MapT?.DarkModeColor : tech?.MapT?.TechnologyColor
                      }`
                    }}>
                    {tech?.MapT?.Technology}
                  </li>
                ))
              : []}
          </ul>
        ),
        action: (
          <div className="flex align-items-center">
            <Switch UserStatus={user.UserStatus} onChange={(e) => handleActive(e, user.ID)} />
            <ActionButtons
              onClick={(actionType) => handleRowActions(actionType, user.ID, name)}
              avaibility
            />
          </div>
        )
      };
    });
    setInterviewerList(modifiedList);
  }, [interviewers, isDarkMode()]);

  useEffect(() => {
    const modifiedList = interviewers?.data?.map((user) => {
      const name = `${wordCapitalize(user.FirstName)} ${wordCapitalize(user.LastName)}`;
      return {
        Experience:
          `${user.YearsOfExperience} Years` +
          `${user?.MonthsOfExperience ? ` ${user?.MonthsOfExperience} Months` : ''}`,
        Name: name,
        'Technical Skills': user.TechStack?.map((tech) => wordCapitalize(tech)).join(', ')
      };
    });
    setInterviewerExport(modifiedList);
  }, [interviewers]);

  useEffect(() => {
    interviewerId && dispatch(userDetailsAction(interviewerId));
  }, [dispatch, interviewerId]);

  const modifyUserDetails = useCallback(() => {
    if (userDetails?.ID) {
      setInterviewerDetails([
        {
          title: 'Name',
          value: `${wordCapitalize(userDetails?.FirstName || '')} ${wordCapitalize(
            userDetails?.LastName || ''
          )}`
        },
        { title: 'Role', value: getUserRole(userDetails?.UserTypeId || '') },
        {
          title: 'Gender',
          value: userDetails?.Gender == 1 ? 'Male' : userDetails?.Gender == 2 ? 'Female' : '--'
        },
        {
          title: 'Skills',
          value: userDetails?.UMap?.length
            ? userDetails?.UMap?.map((tech) => wordCapitalize(tech?.MapT?.Technology)).join(' | ')
            : ''
        },
        {
          title: 'Experience',
          value: formatExperience(userDetails?.YearsOfExperience, userDetails?.MonthsOfExperience)
        },
        {
          title: 'Will take Practical Round?',
          value: userDetails.IsPracticalAllow ? 'Yes' : 'No'
        },
        {
          title: 'Is Admin of Practical Team?',
          value: userDetails.IsPracticalAdmin ? 'Yes' : 'No'
        },
        {
          title: 'Email Address',
          value: (
            <div className="center">
              <span>{userDetails?.Email || '--'}</span>
              {userDetails?.Email && <CopyToClipboard text={userDetails?.Email} />}
            </div>
          )
        },
        {
          title: `PM's Email Address`,
          value: (
            <div className="center">
              <span>{userDetails?.ProjectManagerEmail || '--'}</span>
              {userDetails?.ProjectManagerEmail && (
                <CopyToClipboard text={userDetails?.ProjectManagerEmail} />
              )}
            </div>
          )
        },
        {
          title: 'Mobile',
          value: (
            <div className="center">
              <span>{userDetails?.Mobile || '--'}</span>
              {userDetails?.Mobile && <CopyToClipboard text={userDetails?.Mobile} />}
            </div>
          )
        },
        {
          title: 'Create on',
          value:
            userDetails?.DateCreated &&
            `${getFormattedDateView(userDetails?.DateCreated)} ${formatTime(
              userDetails?.DateCreated
            )} `
        },
        {
          title: 'Status',
          value: `${
            userDetails?.UserStatus ? (userDetails.UserStatus === 1 ? 'Active' : 'Inactive') : ''
          }${
            userDetails?.UserStatusUpdatedAt
              ? ` since ${getFormattedDateView(userDetails.UserStatusUpdatedAt)} ${formatTime(
                  userDetails.UserStatusUpdatedAt
                )}`
              : ''
          }`
        }
      ]);
    }
  }, [userDetails]);

  useEffect(() => {
    modifyUserDetails();
  }, [userDetails, modifyUserDetails]);

  const handlePagination = (pageNumber, size) =>
    setTableStates((prevState) => {
      return {
        ...prevState,
        pageNumber,
        size
      };
    });

  useEffect(() => {
    const totalPages = Math.ceil(interviewers.pagination.totalRecords / tableStates.size);

    // If current page is no longer valid → reset to page 1
    if (tableStates.pageNumber > totalPages && totalPages > 0) {
      setTableStates((prev) => ({
        ...prev,
        pageNumber: 1
      }));
    }
  }, [interviewers.pagination.totalRecords]);

  const handleSearch = (searchValue) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        searchValue
      };
    });
  };

  const handleSort = (sortBy) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        sortBy,
        orderBy: prevState.orderBy === 'asc' ? 'desc' : 'asc'
      };
    });
  };

  const handleRowActions = (actionType, rowId, name) => {
    if (actionType === ACTION_TYPE.VIEW) {
      onTogglePopup();
      setInterviewerId(rowId);
    } else if (actionType === ACTION_TYPE.EDIT) {
      onToggleSiderbar();
      setEditInterviewerId(rowId);
      dispatch(editUserDetailsAction(rowId));
      document.body.classList.add('sidebar-open');
    } else if (actionType === ACTION_TYPE.AVAILABILITY) {
      setShowEditInterviewerAvailabilityPopup(true);
      setEditInterviewerId(rowId);
      setSelectedInterviewerName(name);
    } else if (actionType === ACTION_TYPE.DELETE) {
      confirmDialog({
        header: 'Delete',
        message: 'Are you sure you want to delete?',
        icon: 'pi pi-exclamation-triangle',
        accept: () => dispatch(deleteInterviewer(rowId))
      });
    }
  };

  const handleView = (ID) => {
    if (ID) {
      onTogglePopup();
      setInterviewerId(ID);
    }
  };

  const handleActive = (event, id) => {
    dispatch(userStatus({ id, UserStatus: event.value }));
  };

  const handleAdd = () => {
    setEditInterviewerId(null);
    onToggleSiderbar();
    setVisibleRight(true);
    document.body.classList.add('sidebar-open');
  };

  const handleStatusFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        technology: value || ''
      };
    });
  };

  const handlePracticalOptionFilter = (value) => {
    setTableStates((prevState) => {
      return {
        ...prevState,
        practicalOptionFilter: value || null
      };
    });
  };

  return (
    <>
      <UserDetails
        header="Interviewer Details"
        data={interviewerDetails}
        onHide={onTogglePopup}
        show={showPopup}
      />
      {showEditInterviewerAvailabilityPopup && (
        <EditInterviewerAvailability
          onHide={() => setShowEditInterviewerAvailabilityPopup(false)}
          show={showEditInterviewerAvailabilityPopup}
          name={selectedInterviewerName}
          ID={editInterviewerId}
        />
      )}
      <AddEditInterviewer ID={editInterviewerId} onHide={onToggleSiderbar} show={visibleRight} />
      <DataTable
        name={TABLE_NAME.INTERVIEWERS}
        searchPlaceHolder="Search by Interviewer name, Email and Mobile"
        columns={INTERVIEWERS_COLUMNS}
        data={interviewerList}
        exportData={interviewerExport}
        totalRecords={interviewers?.pagination?.totalRecords}
        onPagination={handlePagination}
        pageNumber={tableStates.pageNumber}
        pageSize={tableStates.size}
        searchValue={tableStates.searchValue}
        onSearch={debounce(handleSearch, DEBOUNCE_TIMEOUT)}
        onSort={handleSort}
        onActive={handleActive}
        onAdd={() => handleAdd()}
        isDropdownMultiSelect
        dropdownPlaceHolder="Select Technology"
        onStatusFilter={handleStatusFilter}
        dropdown={
          technologyListData?.length
            ? technologyListData.map((tech) => ({ name: tech.Technology, value: tech.ID }))
            : []
        }
        dropdownValue={tableStates.technology}
        thirdDropdown={INTERVIEWER_FILTER_DROPDOWN_OPTIONS || []}
        thirdDropdownValue={tableStates.practicalOptionFilter}
        onThirdDropdownChange={handlePracticalOptionFilter}
        isThirdDropdownMultiSelect
        clearFilter
        isClearFilterDisabled={
          JSON.stringify(normalizeObject(interviewersTableStates)) ===
          JSON.stringify(normalizeObject(tableStates))
        }
        clearFilterOnClick={() => {
          setTableStates({
            ...interviewersTableStates,
            pageNumber: INITIAL_PAGE_NUMBER,
            size: INITIAL_SIZE
          });
          localStorage.removeItem(INTERVIEWER_FILTER_KEY);
        }}
      />
    </>
  );
};

export default InterviewerList;
